<div class="order-page" [attr.data-view]="view">
  <div id="head"></div>

  <!-- The order as the cart's boxes (owner 2026-10-12): the head, the order's details and the downloads in one box,
       getting started and the notes each in their own under it -->
  <section class="order-box order-box-main">
    <header class="order-head">
      <span class="order-eyebrow" [ngSwitch]="view">
        <app-icon class="status-paid" name="circle-check" *ngSwitchCase="'paid'" aria-hidden="true"></app-icon>
        <app-icon name="hourglass-half" *ngSwitchCase="'pending'" aria-hidden="true"></app-icon>
        <app-icon name="hourglass-half" *ngSwitchCase="'loading'" aria-hidden="true"></app-icon>
        <app-icon class="status-muted" name="magnifying-glass" *ngSwitchCase="'not-found'" aria-hidden="true"></app-icon>
        <app-icon class="status-warn" name="circle-exclamation" *ngSwitchDefault aria-hidden="true"></app-icon>
        Order
      </span>
      <div class="order-title-row">
        <h1 class="order-title">{{ title }}</h1>
        <span class="order-number" *ngIf="order?.orderNumber" [attr.aria-label]="'Order ' + order?.orderNumber">
          <span class="order-number-value">{{ order?.orderNumber }}</span>
        </span>
      </div>
      <p class="order-lead">{{ subtitle }}</p>
    </header>

  <!-- ============================================================ pending -->
  <section class="order-panel order-pending" *ngIf="view === 'pending' || view === 'loading'" aria-live="polite">
    <div class="pending-spinner" *ngIf="!stillConfirming" aria-hidden="true">
      <span></span><span></span><span></span>
    </div>
    <p class="pending-text" *ngIf="view === 'loading'">Loading your order…</p>
    <p class="pending-text" *ngIf="view === 'pending' && !stillConfirming">
      Keep this page open. Your download buttons appear here the moment Stripe confirms the payment.
    </p>
    <ng-container *ngIf="stillConfirming">
      <p class="pending-text">
        Some payment methods take a little longer to settle. You can close this page: the links will be in your inbox,
        and this page's address stays your order link.
      </p>
      <button type="button" class="codigrate-button order-action" (click)="refresh()" [disabled]="refreshing">
        <p>{{ refreshing ? 'Checking…' : 'Check again' }}</p>
      </button>
    </ng-container>
  </section>

  <!-- =============================================================== paid -->
  <ng-container *ngIf="view === 'paid' && order">

    <!-- Order summary strip -->
    <section class="order-panel order-meta" aria-label="Order details">
      <dl class="order-meta-grid">
        <div>
          <dt>Order</dt>
          <dd>{{ order.orderNumber }}</dd>
        </div>
        <div *ngIf="paidAtLabel">
          <dt>{{ free ? 'Ordered on' : 'Paid on' }}</dt>
          <dd>{{ paidAtLabel }}</dd>
        </div>
        <div *ngIf="totalLabel">
          <dt>Total</dt>
          <dd>{{ totalLabel }} <span class="order-meta-sub" *ngIf="!free">incl. VAT</span></dd>
        </div>
        <div *ngIf="order.receiptUrl">
          <dt>Receipt</dt>
          <dd><a [href]="order.receiptUrl" target="_blank" rel="noopener noreferrer" class="theme-color-link">
            <app-icon name="receipt" aria-hidden="true"></app-icon> View receipt</a></dd>
        </div>
      </dl>
      <p class="order-meta-mail" *ngIf="emailLine">
        <app-icon name="envelope-open-text" aria-hidden="true"></app-icon>
        <span>{{ emailLine }}{{ free ? '' : ' Your receipt arrives from Stripe in a separate e-mail.' }}</span>
      </p>
      <!-- every copy in one zip (owner, 2026-09-21), a folder each (owner, 2026-09-25), when there is more than one -->
      <p class="order-download-all" *ngIf="downloadAllUrl">
        <a class="download-all-cta" [href]="downloadAllUrl" [attr.download]="'Codigrate order ' + order.orderNumber + '.zip'" (click)="downloadAll($event)">
          <app-icon name="download" aria-hidden="true"></app-icon>
          <span>Download all ({{ copyCount }} {{ hasApp ? ( copyCount === 1 ? 'file' : 'files' ) : ( copyCount === 1 ? 'theme' : 'themes' ) }} in one zip)</span>
        </a>
      </p>
    </section>

    <!-- Purchased copies: the cart panel again, one row per copy (each its own numbered file), with the download and the install steps -->
    <section class="order-lines" aria-label="Your downloads">
      <app-store-item-card *ngFor="let line of lines; trackBy: trackByCopy"
                           [product]="line.product"
                           [category]="line.category"
                           [productName]="line.productName"
                           [platformName]="line.platformName"
                           [platformLogo]="line.platformLogo" [palette]="line.palette"
                           [app]="line.app" [appUrl]="line.appUrl">

        <div cardActions class="line-actions">
          <span class="line-free" *ngIf="line.item.free">Free</span>
          <span class="line-copies" *ngIf="copyCount > 1">{{ line.copy.number }} of {{ copyCount }}</span>
          <a class="download-cta"
             [href]="line.copy.downloadUrl || '#'"
             [attr.download]="line.copy.fileName"
             [class.disabled]="!line.copy.downloadUrl || refreshing || line.exhausted"
             [attr.aria-disabled]="!line.copy.downloadUrl || refreshing || line.exhausted"
             (click)="download(line, $event)">
            <app-icon name="download" class="download-cta-icon" aria-hidden="true"></app-icon>
            <span>{{ line.exhausted ? 'Limit reached' : (refreshing ? 'Preparing…' : 'Download') }}</span>
            <span class="download-cta-arrow" aria-hidden="true" *ngIf="!line.exhausted">&rarr;</span>
          </a>
        </div>

        <!-- the same middle column as the cart row (owner, 2026-09-20: consistent, no file line): the licence,
             the version under it; the file itself is named after the order and the copy when it downloads -->
        <div cardMeta class="line-meta">
          <p class="line-licence">Lifetime access<span class="line-version" *ngIf="line.item.version">ver. {{ line.item.version }}</span></p>
        </div>

        <!-- under the row (after the card's own tag pills): the download notes, then the item's rating
             (owner, 2026-10-04: one per line, on its first copy's row) -->
        <div cardFooter class="line-footer" *ngIf="line.remainingNote || line.exhausted || (order.reviewable && line.firstOfItem)">
          <p class="line-note" *ngIf="line.remainingNote">{{ line.remainingNote }}</p>
          <p class="line-warning" *ngIf="line.exhausted">
            This item has reached its download limit. <a [routerLink]="URLS.contactUs" class="theme-color-link">Contact us</a> with your order number and we'll reset it.
          </p>
          <app-store-review-form *ngIf="order.reviewable && line.firstOfItem"
                                 [token]="order.token"
                                 [item]="line.item"
                                 [defaultName]="order.reviewName ?? null"
                                 (saved)="onReviewSaved(line, $event)"></app-store-review-form>
        </div>
      </app-store-item-card>
    </section>

  </ng-container>

  <!-- ============================================== failed / expired / etc. -->
  <section class="order-panel order-notice" *ngIf="view === 'failed' || view === 'expired'">
    <a class="codigrate-button order-action" [routerLink]="URLS.cart"><p>Back to your cart</p></a>
  </section>

  <section class="order-panel order-notice" *ngIf="view === 'refunded' || view === 'disputed'">
    <p>If you think this is a mistake, <a [routerLink]="URLS.contactUs" class="theme-color-link">contact us</a>{{ order?.orderNumber ? ' and quote ' + order?.orderNumber : '' }}.</p>
  </section>

  <section class="order-panel order-notice" *ngIf="view === 'not-found'">
    <p>Paid already? We can e-mail your order links again.</p>
    <a class="codigrate-button order-action" [routerLink]="URLS.orderFind"><p>Find my order</p></a>
  </section>

  <section class="order-panel order-notice" *ngIf="view === 'error'">
    <a class="codigrate-button order-action" [routerLink]="URLS.root"><p>Back to home page</p></a>
  </section>
  </section>

  <ng-container *ngIf="view === 'paid' && order">
    <!-- Getting started: one box per platform in the order (three Sublime themes = one Sublime box), always open -->
    <section class="order-box order-box-plain" *ngIf="guides.length">
      <app-store-guides class="order-guides" [guides]="guides"></app-store-guides>
    </section>

    <section class="order-box order-footnote">
      <ul class="footnote-list">
        <li><app-icon name="circle-info" aria-hidden="true"></app-icon><span>Always the latest version. When {{ hasApp ? 'an app or a theme' : 'a theme' }} is updated, these downloads deliver the new one.</span></li>
        <!-- the text in one span: the li is a flex row, so bare text, a link and a trailing "." would each be an item with the gap between them -->
        <li *ngIf="!free"><app-icon name="circle-info" aria-hidden="true"></app-icon><span>Personal licence. Digital download, so all sales are final (<a [routerLink]="URLS.storeTerms" class="theme-color-link">Terms of Sale</a>).</span></li>
        <li *ngIf="free"><app-icon name="circle-info" aria-hidden="true"></app-icon><span>Free themes are under the <a href="https://opensource.org/license/mit" target="_blank" rel="noopener noreferrer" class="theme-color-link">MIT licence</a>.</span></li>
      </ul>
      <p class="footnote-help">Questions? <a [routerLink]="URLS.contactUs" class="theme-color-link">Contact us</a> and quote {{ order.orderNumber }}.</p>
    </section>
  </ng-container>

</div>
